/**
 * 台股數據 API 服務
 * 使用台灣證券交易所 (TWSE) 官方 API
 */

import { Stock } from '../types';
import { twseApiService } from './TwseApiService';
import { logger } from '../utils/logger';
import { ErrorHandler } from '../utils/errorHandler';
import { RetryHelper } from '../utils/retryHelper';
import { errorMonitoring } from '../utils/errorMonitoring';
import { stockDataCache } from '../utils/cacheService';
import { StockDataTransformer } from '../utils/stockDataTransformer';

// API 配置
const API_CONFIG = {
  // API 請求限制
  REQUEST_TIMEOUT: 10000, // 10秒
  MAX_RETRIES: 3,
  CACHE_DURATION: 5 * 60 * 1000, // 5分鐘快取
};

// 已移除硬編碼的股票清單，現在直接使用 TWSE STOCK_DAY_ALL API

// 使用統一緩存服務替代本地緩存實現

export class StockApiService {
  /**
   * 獲取單一股票資訊
   */
  static async getStockQuote(symbol: string): Promise<Stock | null> {
    try {
      const cacheKey = `quote_${symbol}`;
      const cachedData = stockDataCache.get(cacheKey) as Stock | null;
      
      if (cachedData) {
        logger.api.info('Retrieved stock data from cache', { symbol });
        return cachedData;
      }

      logger.api.info('Fetching stock data from TWSE API', { symbol });
      
      // 使用重試機制調用 API
      const stock = await RetryHelper.apiRetry(async () => {
        const twseData = await twseApiService.getStockPrice(symbol);
        
        if (!twseData) {
          throw new Error(`Unable to get stock data for ${symbol}`);
        }

        return StockDataTransformer.transformToStock(twseData);
      }, {
        maxRetries: 1, // 降低重試次數避免過度重試
        baseDelay: 3000, // 增加重試間隔
        onRetry: (error, attempt) => {
          // 只在最後一次重試時記錄，減少日誌噪音
          if (attempt === 1) {
            logger.api.warn(`Stock quote final retry for ${symbol}`, { error: error?.message });
          }
        }
      });
      
      // 快取結果
      stockDataCache.set(cacheKey, stock);
      
      return stock;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.getStockQuote');
      logger.api.error('Failed to get stock quote', { symbol, error: appError });
      
      // 只對不是"stock not found"的錯誤進行報告，避免過度報告
      const errorMessage = (error as Error)?.message?.toLowerCase() || '';
      if (!errorMessage.includes('unable to get stock data') && 
          !errorMessage.includes('stock not found') &&
          !errorMessage.includes('not found in current data set')) {
        errorMonitoring.reportError(error instanceof Error ? error : new Error(String(error)), {
          component: 'StockApiService',
          category: 'api',
          severity: 'medium',
          metadata: {
            method: 'getStockQuote',
            symbol
          }
        });
      }
      
      return null;
    }
  }

  /**
   * 獲取多支股票資訊
   */
  static async getMultipleStocks(symbols: string[]): Promise<Stock[]> {
    logger.api.info('Getting multiple stocks', { 
      count: symbols.length,
      sampleSymbols: symbols.slice(0, 5).join(', ') + (symbols.length > 5 ? '...' : '')
    });
    
    try {
      const cacheKey = `multiple_${[...symbols].sort().join(',')}`; // DATA-08:複製後排序,不變動呼叫端陣列
      const cachedData = stockDataCache.get(cacheKey) as Stock[] | null;
      
      if (cachedData) {
        logger.api.info('Retrieved multiple stocks from cache', { count: symbols.length });
        return cachedData;
      }

      logger.api.info('Fetching multiple stocks from TWSE API', { count: symbols.length });
      
      const allPrices = await twseApiService.getCurrentStockPrices();
      logger.api.info('TWSE API returned stock prices', { priceCount: allPrices.length });
      
      const filteredPrices = allPrices.filter(price => symbols.includes(price.stockCode));
      const stocks = StockDataTransformer.transformBatchToStocks(filteredPrices);
      
      logger.api.info('Filtered and transformed stocks', { resultCount: stocks.length });
      
      // 快取結果
      stockDataCache.set(cacheKey, stocks);
      logger.api.info('Cached multiple stocks result');
      return stocks;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.getMultipleStocks');
      logger.api.error('Failed to get multiple stocks', { error: appError });
      return [];
    }
  }

  /**
   * 獲取所有台股數據（使用 TWSE STOCK_DAY_ALL API）
   */
  static async getTaiwanStocks(): Promise<Stock[]> {
    logger.api.info('Getting all Taiwan stocks using STOCK_DAY_ALL API');
    
    try {
      const cacheKey = 'all_taiwan_stocks';
      const cachedData = stockDataCache.get(cacheKey) as Stock[] | null;
      
      if (cachedData) {
        logger.api.info('Retrieved all Taiwan stocks from cache', { count: cachedData.length });
        return cachedData;
      }

      logger.api.info('Fetching all Taiwan stocks from TWSE API');
      
      // 直接使用 TWSE API 的 STOCK_DAY_ALL 獲取所有股票
      const allPrices = await twseApiService.getCurrentStockPrices();
      logger.api.info('TWSE API returned all stock prices', { count: allPrices.length });
      
      // 使用統一轉換工具轉換為 Stock 格式
      const stocks = StockDataTransformer.transformBatchToStocks(allPrices);
      
      logger.api.info('Transformed stock data', { transformedCount: stocks.length });
      
      // 快取結果
      stockDataCache.set(cacheKey, stocks);
      logger.api.info('Cached Taiwan stocks result');
      
      logger.api.info('Completed getTaiwanStocks', { returnCount: stocks.length });
      return stocks;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.getTaiwanStocks');
      logger.api.error('Failed to get Taiwan stocks', { error: appError });
      return [];
    }
  }

  /**
   * 搜尋股票
   */
  static async searchStocks(searchTerm: string): Promise<Stock[]> {
    try {
      // 先從台股清單中搜尋
      const taiwanStocks = await this.getTaiwanStocks();
      
      return taiwanStocks.filter(stock =>
        stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.searchStocks');
      logger.api.error('Failed to search stocks', { error: appError });
      return [];
    }
  }

  /**
   * 取得股票歷史資料 (從所有股票的當日數據中篩選)
   * 注意：官方 OpenAPI 只提供當日所有股票的資料，沒有單一股票的歷史查詢功能
   */
  static async getStockHistory(symbol: string, period: '1D' | '5D' | '1M' | '3M' | '6M' | '1Y' = '1M') {
    try {
      logger.api.warn('TWSE OpenAPI limitation: only current day data available', { symbol });
      
      // 從當日資料中取得該股票資訊
      const allStocksData = await twseApiService.getAllStocksDayData();
      const stockData = allStocksData.find(stock => stock.stockCode === symbol);
      
      if (!stockData) {
        logger.api.warn('Stock not found in current day data', { symbol });
        return null;
      }

      // 返回當日資料（格式化為歷史資料格式）
      return [{
        date: stockData.date,
        open: stockData.openPrice,
        high: stockData.highPrice,
        low: stockData.lowPrice,
        close: stockData.closePrice,
        volume: stockData.volume,
      }];
      
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.getStockHistory');
      logger.api.error('Failed to get stock history', { symbol, error: appError });
      return null;
    }
  }

  /**
   * 清除快取
   */
  static clearCache(): void {
    stockDataCache.clear();
    logger.api.info('Stock data cache cleared');
  }

  /**
   * 取得快取統計
   */
  static getCacheStats() {
    return {
      size: stockDataCache.size(),
      cacheDuration: API_CONFIG.CACHE_DURATION,
    };
  }

  /**
   * 檢查 API 健康狀態
   */
  static async checkApiHealth(): Promise<boolean> {
    try {
      return await twseApiService.isApiAvailable();
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockApiService.checkApiHealth');
      logger.api.error('TWSE API health check failed', { error: appError });
      return false;
    }
  }
}
